/**
 * Store: Gift with purchase
 * -----------------------------------------------------------------------------
 * Section settings so they're globally available.
 *
 * States:
 * - `active` - Promotions whose conditions have been met.
 * - `added` - Active promotions that have been added to the cart.
 * - `available` - Active promotions that haven't been added to the cart.
 * - `inactive` - Promotions in cart that aren't active and should be removed.
 *
 */
/* eslint-disable no-underscore-dangle */
export default {
  namespaced: true,

  state: () => ({
    active: [],
    added: [],
    available: [],
    dismissed: [],
    id: '',
    inactive: [],
    pending: [],
    newAvailable: false,
    promotions: [],
    products: {
      available: [],
    },
    settings: {
      enable: false,
      excludedProductTypes: [],
      minimumPrice: 0,
      priceRounding: true,
    },
  }),

  getters: {

    getLinkedPromotionProduct:(state) => (handle, key) => {
      if (!handle || !key) {
        return null
      }

      return state.products[key]?.find(
        product => product?.promotion?.trigger?.value === handle,
      )
    },

    /**
     * Get cart that only returns products that are not excluded or gifts.
     * - Also returns updated cart total and items count.
     * @returns {Object}
     */
    getApplicableCart(state, _, rootState) {
      const items = []
      let totalPrice = 0

      rootState.cart.response.items.forEach((item) => {
        const applicable =
          !item.properties?.__gift &&
          !state.settings.excludedProductTypes.includes(item.product_type)

        if (!applicable) {
          return
        }

        totalPrice += item.final_line_price
        items.push(item)
      })

      return {
        item_count: items.length,
        items,
        total_price: totalPrice,
      }
    },

    /**
     * Get if the current promotion ID has been previously dismissed.
     * @returns {Boolean}
     */
    getIdDismissed(state) {
      return state.dismissed.includes(state.id)
    },

    /**
     * Get products with assigned gift products.
     * - Handle object `isFree` is true if gift product is free.
     * @returns {Object}
     */
    getProductsWithGift(state) {
      const products = {}

      state.promotions.forEach((promotion) => {
        if (promotion.trigger.type !== 'product') {
          return
        }

        products[promotion.trigger.value] = {
          isFree:
            promotion.action.discount === 100 &&
            !state.settings.minimumPrice,
        }
      })

      return products
    },

    /**
     * Get threshold values.
     * @returns {Array}
     */
    getThresholds(state) {
      const thresholds = []

      state.promotions.forEach((promotion) => {
        if (promotion.trigger.type !== 'threshold') {
          return
        }

        thresholds.push(promotion.trigger.value)
      })

      return thresholds
    },

    getAllPromotionStates(state) {
      return {
        active: state.active,
        added: state.added,
        available: state.available,
        dismissed: state.dismissed,
        inactive: state.inactive,
      }
    },
  },

  mutations: {

    /**
     * Set `active` promotions.
     * @param {Array} active - Promotions whose conditions have been met.
     */
    SET_ACTIVE(state, active) {
      state.active = active
    },

    /**
     * Set `added` promotions.
     * @param {Array} added - Active promotions that have been added to cart.
     */
    SET_ADDED(state, added) {
      state.added = added
    },

    /**
     * Set `available` promotions.
     * @param {Array} available - Active promotions that aren't in cart.
     */
    SET_AVAILABLE(state, available) {
      state.available = available
    },

    /**
     * Set dismissed.
     * @param {String} id - ID to set as dismissed.
     */
    SET_DISMISSED(state, id) {
      state.dismissed.push(id)
    },

    /**
     * Set unique ID for current available promotions.
     * @param {String} id - Current promotions ID.
     */
    SET_ID(state, id) {
      state.id = id
    },

    /**
     * Set `inactive`.
     * @param {Array} inactive - Promotions in the cart that aren't active and
     * should be removed.
     */
    SET_INACTIVE(state, inactive) {
      state.inactive = inactive
    },

    /**
     * Set `pending`.
     * @param {Array} pending - Promotions that are yet to be realised (thresholds)
     */
    SET_PENDING(state, pending) {
      state.pending = pending
    },

    /**
     * Set promotions.
     * @param {Array} promotions - All promotions.
     */
    SET_PROMOTIONS(state, promotions) {
      state.promotions = promotions
    },

    /**
     * Set settings.
     * @param {Object} settings - Gift with purchase settings.
     */
    SET_SETTINGS(state, settings) {
      state.settings = settings
    },

    /**
     * Set products.
     * @param {Array} products - Array of Shopify product objects.
     * @param {String} key - Selector for which products to be updated.
     */
    SET_PRODUCTS(state, { key, products }) {
      state.products[key] = products
    },
  },

  actions: {

    /**
     * Set products.
     */
    setProducts({ commit }, { key, products }) {
      commit('SET_PRODUCTS', { key, products } )
    },

    /**
     * Set dismissed.
     */
    setDismissed({ commit, state }) {
      commit('SET_DISMISSED', state.id)
    },

    /**
     * Set unique ID for current available promotions.
     * - Means the dismissed state will only apply if the available promotions
     *   haven't changed.
     */
    setId({ commit, state }) {
      const id = state.available.length
        ? state.available.map((promotion) => promotion.id).join('--')
        : 'gift-empty-modal'

      commit('SET_ID', id)
    },

    /**
     * Set promotions.
     * @param {Object} payload - Payload data.
     * @param {Array} payload.blocks - Section blocks passed to Vue.
     * @param {Object} payload.settings - Section settings passed to Vue.
     * @param {String} payload.currentMarketHandle - Current market handle.
     */
    setPromotions({ commit }, { blocks, settings, currentMarketHandle }) {
      const promotions = blocks.map((block, index) => {
        if (!block.trigger) {
          return false
        }

        /**
         * Grab next block for action.
         */
        const action = blocks[index + 1]

        if (!action) {
          return false
        }

        /**
         * Check if block should be active for current market.
         * If marketHandle is specified and doesn't match current market, skip this block.
         */
        if (block.marketHandle && block.marketHandle !== currentMarketHandle) {
          return false
        }

        if (action.marketHandle && action.marketHandle !== currentMarketHandle) {
          return false
        }

        /**
         * Build name specific data.
         */
        const triggerObject = {
          type: block.type,
          value: block[block.type],
        }

        switch (block.type) {
          case 'collection':
            triggerObject.quantity = block.quantity
            break

          case 'threshold':
            triggerObject.value = Number(block[block.type])
            break

          case 'product':
            break
        }

        const promotion = {
          action: {
            discount: action.discount || 100,
            type: action.type,
            value: action[action.type],
          },
          id: `${block.type}--${block.id}--${action[action.type]}--${action.discount || 100}`,
          trigger: triggerObject,
        }

        /**
         * Return promotion object.
         */
        return promotion
      }).filter(Boolean)

      commit('SET_SETTINGS', settings)
      commit('SET_PROMOTIONS', promotions)
    },

    /**
     * Set promotion states.
     * - Goes through each promotion and determines its state based on cart.
     * - Non-active is used to keep the opposites of `active`, `inactive` is
     *   only for promotions which are in the cart when they shouldn't be.
     */
    setStates({ commit, dispatch, getters, rootState, state }) {
      const active = []
      const added = []
      const available = []
      const nonActive = []
      const pending = []

      const cartItems = rootState.cart.response.items
      const applicableCartItems = getters.getApplicableCart.items
      const applicableCartTotal = getters.getApplicableCart.total_price

      /**
       * Find `active` or non-active promotions.
       * - Collection: find total quantity of items in trigger collection.
       * - Product: find if trigger product is in cart and not a gift.
       * - Threshold: if cart total is above trigger threshold.
       */
      state.promotions.forEach((promotion) => {
        let conditionsMet = false
        let quantity = 0

        switch (promotion.trigger.type) {
          case 'collection':
            applicableCartItems.forEach((item) => {
              const collections = item?.collections?.map(collection => collection.toLowerCase())
              if (!collections.includes(promotion.trigger.value)) {
                return
              }

              quantity += item.quantity
            })

            conditionsMet = quantity >= promotion.trigger.quantity
            break

          case 'product':
            const foundProduct = applicableCartItems.find((item) => {
              return (
                item.handle === promotion.trigger.value &&
                !item.properties?.__gift
              )
            })
            conditionsMet = !!foundProduct
            break

          case 'threshold':
            conditionsMet = applicableCartTotal >= promotion.trigger.value
            break
        }

        if (conditionsMet) {
          active.push(promotion)
          return
        }

        if (promotion.trigger.type !== 'product') {
          pending.push(promotion)
        }

        nonActive.push(promotion)
      })

      /**
       * Go through active and determine if they're `added` or `available`.
       */
      active.forEach((promotion) => {
        const inCart = cartItems.find((item) => {
          return item.properties?.__gift === promotion.id
        })

        if (inCart) {
          added.push(promotion)
          return
        }

        available.push(promotion)
      })

      /**
       * Go through cart items.
       * - If they have a promotion ID which matches a non-active promotion then
       *   add to inactive for removal.
       */
      const inactive = nonActive.map((promotion) => {
        const inCart = cartItems.find((item) => {
          return item.properties?.__gift === promotion.id
        })

        if (!inCart) {
          return false
        }

        return promotion
      }).filter(Boolean)

      /**
       * Commit to states.
       */
      commit('SET_ACTIVE', active)
      commit('SET_ADDED', added)
      commit('SET_AVAILABLE', available)
      commit('SET_INACTIVE', inactive)
      commit('SET_PENDING', pending)

      /**
       * Dispatch actions.
       */
      dispatch('setId')
    },
  },
}
